const toDateInstance = (value) => {
  if (!value) {
    return null;
  }

  const candidate = value instanceof Date ? value : new Date(value);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
};

const formatServiceDate = (value) => {
  const date = toDateInstance(value);
  if (!date) {
    return '—';
  }

  return date.toLocaleDateString('en-GB').replaceAll('/', '-');
};

const calculateDurationParts = (start, end = new Date()) => {
  const startDate = toDateInstance(start);
  const endDate = toDateInstance(end);

  if (!startDate || !endDate || endDate < startDate) {
    return null;
  }

  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  let days = endDate.getDate() - startDate.getDate();

  if (days < 0) {
    const previousMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 0);
    days += previousMonth.getDate();
    months -= 1;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const totalDays = Math.max(
    0,
    Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)),
  );

  return { years, months, days, totalDays };
};

const convertDaysToParts = (totalDays = 0) => {
  const safeDays = Math.max(0, Math.floor(totalDays));
  const years = Math.floor(safeDays / 365);
  const remainingAfterYears = safeDays % 365;
  const months = Math.floor(remainingAfterYears / 30);
  const days = remainingAfterYears % 30;

  return { years, months, days, totalDays: safeDays };
};

const formatDurationParts = (metrics) => {
  if (!metrics) {
    return '—';
  }

  const { years = 0, months = 0, days = 0 } = metrics;

  const formatUnit = (value, unit) => `${value} ${value === 1 ? unit : `${unit}s`}`;

  return [
    formatUnit(years, 'year'),
    formatUnit(months, 'month'),
    formatUnit(days, 'day'),
  ].join(' ');
};

const deriveExitType = (reason) => {
  const normalizedReason = (reason || '').toLowerCase();

  if (!normalizedReason || normalizedReason === 'relieving') {
    return 'Relieved';
  }

  if (normalizedReason === 'long_leave' || normalizedReason === 'long leave') {
    return 'Long Leave from';
  }

  if (normalizedReason === 'others') {
    return 'Others';
  }

  return normalizedReason;
};

const normalizeHistoryEntries = (records = []) =>
  (Array.isArray(records) ? records : [])
    .map((entry, index) => ({
      ...entry,
      id: entry.id ?? `service-${index}`,
      startDate: entry.startDate ?? entry.effectiveDate ?? entry.createdAt ?? null,
      endDate: entry.endDate ?? entry.relievingDate ?? null,
      activationReason: entry.activationReason ?? null,
      activationDescription: entry.activationDescription ?? null,
      inactivationReason: entry.inactivationReason ?? entry.reason ?? null,
      inactivationDescription: entry.inactivationDescription ?? entry.notes ?? null,
      leaveFromDate: entry.leaveFromDate ?? null,
    }))
    .filter((entry) => entry.startDate)
    .sort((first, second) => {
      const firstDate = toDateInstance(first.startDate) ?? new Date(0);
      const secondDate = toDateInstance(second.startDate) ?? new Date(0);
      return firstDate.getTime() - secondDate.getTime();
    });

const formatActivationReasonLabel = (reason, description, startDate) => {
  const labelDate = formatServiceDate(startDate);
  const normalized = (reason || '').toLowerCase();

  if (normalized === 'rejoined') {
    return `Rejoined on ${labelDate}`;
  }

  if (normalized === 'after_long_leave' || normalized === 'after long leave') {
    return `Returned from Long Leave on ${labelDate}`;
  }

  if (normalized === 'others') {
    return description || 'Activation recorded';
  }

  return 'Joined';
};

const formatInactivationReasonLabel = (reason, description) => {
  const normalized = (reason || '').toLowerCase();

  if (normalized === 'others') {
    return description || 'Others';
  }

  if (normalized === 'long_leave' || normalized === 'long leave') {
    return 'Long Leave';
  }

  return 'Relieved';
};

export const buildServiceHistoryTimeline = (
  records = [],
  { defaultStartDate = null, defaultOutlet = null, currentDate = new Date() } = {},
) => {
  const fallbackStart = toDateInstance(defaultStartDate);
  const normalizedEntries = normalizeHistoryEntries(records);

  const periods = normalizedEntries.map((entry, index) => {
    const startDate = toDateInstance(entry.startDate) ?? fallbackStart;
    if (!startDate) {
      return null;
    }

    const leaveFromDate = toDateInstance(entry.leaveFromDate);
    const endDate = toDateInstance(entry.endDate ?? entry.relievingDate) ?? null;
    const status = (entry.status || (endDate ? 'inactive' : 'active')).toLowerCase();
    const isActive = status === 'active' || !endDate;
    const exitDate = isActive ? null : leaveFromDate ?? endDate;
    const durationEnd = isActive ? currentDate : exitDate ?? endDate;
    const duration = calculateDurationParts(startDate, durationEnd);

    if (!duration) {
      return null;
    }

    const exitType = isActive ? 'To Till Now' : deriveExitType(entry.inactivationReason);
    const exitLabel = isActive
      ? 'To Till Now'
      : `${exitType} ${formatServiceDate(exitDate ?? endDate)}`;
    const activationReasonLabel = formatActivationReasonLabel(
      entry.activationReason,
      entry.activationDescription,
      startDate,
    );
    const inactivationReasonLabel = formatInactivationReasonLabel(
      entry.inactivationReason,
      entry.inactivationDescription,
    );
    const reasonLabel = isActive ? activationReasonLabel : inactivationReasonLabel;
    const notesLabel = isActive
      ? entry.activationDescription || ''
      : entry.inactivationDescription || '';

    const joinedLabel = formatServiceDate(startDate);
    const durationLabel = formatDurationParts(duration);

    return {
      ...entry,
      id: entry.id ?? `service-${index}`,
      startDate,
      endDate: endDate ?? durationEnd,
      relievingDate: endDate,
      status: isActive ? 'active' : 'inactive',
      duration,
      exitType,
      exitLabel,
      durationLabel,
      reasonLabel,
      notesLabel,
      reason: reasonLabel,
      notes: notesLabel,
      timelineLabel: `Joined ${joinedLabel} | ${exitLabel} | Duration ${durationLabel}`,
      outlet: entry.outlet ?? defaultOutlet ?? null,
    };
  });

  if (periods.filter(Boolean).length === 0 && fallbackStart) {
    const duration = calculateDurationParts(fallbackStart, currentDate);
    if (duration) {
      const durationLabel = formatDurationParts(duration);
      periods.push({
        id: `service-current-${fallbackStart.toISOString()}`,
        startDate: fallbackStart,
        endDate: currentDate,
        relievingDate: null,
        status: 'active',
        duration,
        exitType: 'To Till Now',
        exitLabel: 'To Till Now',
        durationLabel,
        reasonLabel: formatActivationReasonLabel(null, null, fallbackStart),
        notesLabel: null,
        timelineLabel: `Joined ${formatServiceDate(fallbackStart)} | To Till Now | Duration ${durationLabel}`,
        outlet: defaultOutlet ?? null,
      });
    }
  }

  const sortedPeriods = periods
    .filter((period) => period && period.duration)
    .sort((first, second) => first.startDate.getTime() - second.startDate.getTime());

  const totalDays = sortedPeriods.reduce(
    (sum, period) => sum + (period.duration?.totalDays ?? 0),
    0,
  );
  const totalDuration = convertDaysToParts(totalDays);
  const totalDurationLabel = sortedPeriods.length ? formatDurationParts(totalDuration) : '';

  return {
    periods: sortedPeriods,
    totalDuration,
    totalDurationLabel,
    hasHistory: sortedPeriods.length > 0,
  };
};

export const serviceHistoryUtils = {
  toDateInstance,
  formatServiceDate,
  calculateDurationParts,
  formatDurationParts,
  buildServiceHistoryTimeline,
};

export default serviceHistoryUtils;
